import { useCallback, useRef, useState } from 'react';
import { playSoundEffect } from '../hooks/useSoundEffects';
import {
  buildLiveHealthMapFromGameState,
  shouldClearHealthFxSession,
} from './battlefieldHealthDisplay';
import { HEALTH_FLYOUT_MIN_STEP_MS } from './HealthCountdownFlyout';
const COUNTDOWN_STEP_MS = 110;
const COUNTDOWN_MIN_TOTAL_MS = 600;
const COUNTDOWN_MAX_TOTAL_MS = 1400;
const HEALTH_COUNTDOWN_TICK_SOUND = '/sounds/health_lost.mp3';
const HEALTH_COUNTDOWN_TICK_VOLUME = 0.6;

/** Skip restarting a countdown that is already stepping down to the same target HP. */
export function shouldSkipDuplicateActiveCountdown(priorEntry, toHealth) {
  return Boolean(
    priorEntry?.pulsing === true
    && typeof toHealth === 'number'
    && priorEntry.targetHealth === toHealth
  );
}

/**
 * Lethal follow-up (Redeem AOE, etc.) must cancel an in-flight survivor countdown.
 * Otherwise the next flyout mounts after the unit is already gone from the slot.
 */
export function shouldCancelPulsingCountdownForLethalHold(priorEntry, targetHealth) {
  return Boolean(
    priorEntry?.pulsing === true
    && typeof targetHealth === 'number'
    && targetHealth <= 0
  );
}

function playHealthCountdownTickSound() {
  playSoundEffect(HEALTH_COUNTDOWN_TICK_SOUND, HEALTH_COUNTDOWN_TICK_VOLUME);
}

function createFxEntry(displayHealth, { pulsing = false, targetHealth, startedAt = Date.now(), zoneName = undefined, slotIndex = undefined } = {}) {
  return {
    displayHealth,
    pulsing,
    targetHealth: typeof targetHealth === 'number' ? targetHealth : undefined,
    startedAt,
    zoneName: typeof zoneName === 'string' ? zoneName : undefined,
    slotIndex: typeof slotIndex === 'number' ? slotIndex : undefined,
  };
}

/**
 * Cosmetic HP badge FX after damage — never mutates gameState.
 * Badge reads via badgeHealth; unit.health stays authoritative.
 */
export function useBattlefieldHealthCountdown(animationSpeedMultiplier = 1) {
  const displaysRef = useRef(new Map());
  const timersRef = useRef(new Map());
  const [tick, setTick] = useState(0);
  const [flyoutPulses, setFlyoutPulses] = useState([]);

  const bump = () => setTick((value) => value + 1);

  const emitFlyoutPulse = useCallback((instanceId, value, { zoneName, slotIndex, durationMs } = {}) => {
    if (!instanceId || typeof value !== 'number' || typeof durationMs !== 'number') {
      return;
    }
    setFlyoutPulses((prev) => [
      ...prev.filter((pulse) => pulse.instanceId !== instanceId),
      {
        key: `${instanceId}-${value}-${Date.now()}`,
        instanceId,
        value,
        zoneName,
        slotIndex,
        durationMs,
      },
    ]);
  }, []);

  const clearCountdown = useCallback((instanceId) => {
    if (!instanceId) return;
    const timers = timersRef.current.get(instanceId);
    if (timers) {
      if (timers.intervalId) clearInterval(timers.intervalId);
      if (timers.cleanupId) clearTimeout(timers.cleanupId);
      timersRef.current.delete(instanceId);
    }
    const hadDisplay = displaysRef.current.has(instanceId);
    if (hadDisplay) {
      displaysRef.current.delete(instanceId);
    }
    setFlyoutPulses((prev) => {
      const next = prev.filter((pulse) => pulse.instanceId !== instanceId);
      return next.length === prev.length ? prev : next;
    });
    if (hadDisplay) {
      bump();
    }
  }, []);

  const setHealthHold = useCallback((instanceId, healthBefore, targetHealth, options = {}) => {
    if (!instanceId || typeof healthBefore !== 'number') return;
    const existing = displaysRef.current.get(instanceId);
    if (existing?.pulsing && !shouldCancelPulsingCountdownForLethalHold(existing, targetHealth)) {
      return;
    }
    clearCountdown(instanceId);
    displaysRef.current.set(
      instanceId,
      createFxEntry(healthBefore, {
        targetHealth,
        zoneName: options.zoneName,
        slotIndex: options.slotIndex,
      })
    );
    bump();
  }, [clearCountdown]);

  const startCountdown = useCallback((instanceId, fromHealth, toHealth, options = {}) => {
    if (!instanceId || typeof fromHealth !== 'number' || typeof toHealth !== 'number') {
      return;
    }
    if (toHealth <= 0 || fromHealth <= toHealth) {
      clearCountdown(instanceId);
      return;
    }

    const priorEntry = displaysRef.current.get(instanceId);
    if (shouldSkipDuplicateActiveCountdown(priorEntry, toHealth)) {
      return;
    }
    const zoneName = options.zoneName ?? priorEntry?.zoneName;
    const slotIndex = options.slotIndex ?? priorEntry?.slotIndex;

    clearCountdown(instanceId);
    const speedFactor = animationSpeedMultiplier > 0 ? animationSpeedMultiplier : 1;
    const totalSteps = fromHealth - toHealth;
    const totalDuration = Math.min(
      COUNTDOWN_MAX_TOTAL_MS,
      Math.max(COUNTDOWN_MIN_TOTAL_MS, Math.round((totalSteps * COUNTDOWN_STEP_MS) / speedFactor))
    );
    const perStepBudget = Math.round(totalDuration / totalSteps);
    const stepMs = Math.max(
      Math.round(HEALTH_FLYOUT_MIN_STEP_MS / speedFactor),
      perStepBudget
    );
    const flyoutDurationMs = Math.max(
      Math.round(stepMs * 0.94),
      Math.round((HEALTH_FLYOUT_MIN_STEP_MS * 0.94) / speedFactor)
    );

    let current = fromHealth;
    const startedAt = Date.now();
    displaysRef.current.set(
      instanceId,
      createFxEntry(current, { pulsing: true, targetHealth: toHealth, startedAt, zoneName, slotIndex })
    );
    bump();

    const pushStep = (nextHealth) => {
      displaysRef.current.set(
        instanceId,
        createFxEntry(nextHealth, { pulsing: true, targetHealth: toHealth, startedAt, zoneName, slotIndex })
      );
      emitFlyoutPulse(instanceId, nextHealth, { zoneName, slotIndex, durationMs: flyoutDurationMs });
      bump();
    };

    const runStep = () => {
      current -= 1;
      playHealthCountdownTickSound();
      if (current <= toHealth) {
        pushStep(toHealth);
        const cleanupId = setTimeout(() => {
          displaysRef.current.delete(instanceId);
          timersRef.current.delete(instanceId);
          bump();
        }, Math.round(320 / speedFactor));
        timersRef.current.set(instanceId, { intervalId: null, cleanupId });
        return false;
      }
      pushStep(current);
      return true;
    };

    let intervalId = null;
    if (runStep()) {
      intervalId = setInterval(() => {
        if (!runStep()) {
          clearInterval(intervalId);
        }
      }, stepMs);
      timersRef.current.set(instanceId, { intervalId, cleanupId: null });
    }
  }, [animationSpeedMultiplier, clearCountdown, emitFlyoutPulse]);

  const getHealthDisplayOverride = useCallback((instanceId) => {
    void tick;
    if (!instanceId) return null;
    const entry = displaysRef.current.get(instanceId);
    if (!entry) return null;
    return { health: entry.displayHealth, pulse: entry.pulsing };
  }, [tick]);

  const getHealthFxEntry = useCallback((instanceId) => {
    void tick;
    if (!instanceId) return null;
    return displaysRef.current.get(instanceId) ?? null;
  }, [tick]);

  const reconcileAll = useCallback((liveHealthByInstanceId, context) => {
    let changed = false;
    for (const [instanceId, entry] of displaysRef.current) {
      const liveHealth = liveHealthByInstanceId.get(instanceId);
      if (shouldClearHealthFxSession(instanceId, entry, liveHealth, context)) {
        clearCountdown(instanceId);
        changed = true;
      }
    }
    return changed;
  }, [clearCountdown]);

  const reconcileFromGameState = useCallback((gameState, context) => {
    return reconcileAll(buildLiveHealthMapFromGameState(gameState), context);
  }, [reconcileAll]);

  const resetAll = useCallback(() => {
    for (const [, timers] of timersRef.current) {
      if (timers.intervalId) clearInterval(timers.intervalId);
      if (timers.cleanupId) clearTimeout(timers.cleanupId);
    }
    timersRef.current.clear();
    displaysRef.current.clear();
    setFlyoutPulses([]);
    bump();
  }, []);

  /** Drop attack-damage badge FX at turn boundaries; keep deploy/strategem projectile holds. */
  const releaseStaleAttackDamageFxExceptInstances = useCallback((shouldKeepInstance) => {
    for (const [instanceId] of displaysRef.current) {
      if (typeof shouldKeepInstance === 'function' && shouldKeepInstance(instanceId)) {
        continue;
      }
      clearCountdown(instanceId);
    }
    setFlyoutPulses([]);
  }, [clearCountdown]);

  const dismissFlyoutPulse = useCallback((key) => {
    setFlyoutPulses((prev) => prev.filter((pulse) => pulse.key !== key));
  }, []);

  return {
    setHealthHold,
    startCountdown,
    getHealthDisplayOverride,
    getHealthFxEntry,
    reconcileAll,
    reconcileFromGameState,
    clearCountdown,
    resetAll,
    releaseStaleAttackDamageFxExceptInstances,
    flyoutPulses,
    dismissFlyoutPulse,
  };
}

export default useBattlefieldHealthCountdown;
